import React, { useEffect, useState } from 'react';
import CustomContainer from '../../components/common/CustomContainer';
import { FormGroup, FormLabel, TextField } from '@mui/material';
import moment from 'moment';
import BtnLoader from '../../components/common/BtnLoader';
import { toast } from 'react-toastify';
import { handleKeyPress } from '../../utils/functions/common';
import { vitalMonitoringAddReadingApi, vitalMonitoringGetDeviceApi } from '../../utils/api/services';
import { useSelector } from 'react-redux';
import { RootState } from '../../utils/store';
import Loader from '../../components/common/Loader';

interface DeviceField {
  field_id: number;
  key: string;
  label: string;
  placeholder: string;
  type: string;
  maxLength?: number;
}

interface Device {
  id: number;
  device_name: string;
  fields: DeviceField[];
}

const AddManualVitals: React.FC = () => {
  const [deviceList, setDeviceList] = useState<Device[]>([]);
  const [form, setForm] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const { LoginData } = useSelector((state: RootState) => state.auth);
  const fetchDeviceList = async () => {
    try {
      setLoading(true);
      const result = await vitalMonitoringGetDeviceApi();
      if (result?.success && result.deviceDetails) {
        setDeviceList(result.deviceDetails);

        const initialForm: Record<string, string> = {};
        result.deviceDetails.forEach((device: Device) => {
          device.fields.forEach((field) => {
            initialForm[field.key] = '';
          });
        });
        setForm(initialForm);
      }
    } catch (error: any) {
      toast.error(error?.data?.errors || error?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeviceList();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if ((form.systolic && !form.diastolic) || (!form.systolic && form.diastolic)) {
      return toast.error("Please enter both systolic and diastolic for Blood Pressure");
    }
    const payload = {
      patient_id: LoginData?.loggedPatientId,
      deviceDetails: deviceList.map((device) => {
        const filledFields = device.fields
          .filter((f) => form[f.key])
          .map((f) => ({
            param_key_id: f.field_id,
            param_key_name: f.key,
            param_value: form[f.key],
          }));

        return filledFields.length > 0
          ? { id: device.id, fields: filledFields }
          : null;
      }).filter(Boolean),
      date: moment().format('DD-MM-YYYY HH:mm:ss'),
    };

    if (payload.deviceDetails.length === 0) {
      return toast.error("Please enter at least one vital");
    }

    setBtnLoading(true);
    try {
      await vitalMonitoringAddReadingApi(payload);
      toast.success('Vitals saved successfully!');
      window.history.back();
    } catch (error: any) {
      toast.error(error?.data?.errors || error?.message);
    } finally {
      setBtnLoading(false);
    }
  };

  const multiFieldDevices = deviceList.filter((d) => d.fields.length > 1);
  const singleFieldDevices = deviceList.filter((d) => d.fields.length === 1);

  return (
    <CustomContainer>
      <div className="AllPageMain">
        <form onSubmit={handleSubmit}>
          <div className="HeadAndButton">
            <h2>Add Manual Vitals</h2>
          </div>

          <div className="grid grid-cols-1 gap-8 mt-6">
            {loading ? (
              <Loader />
            ) : (
              <>
                {multiFieldDevices.map((device) => (
                  <div key={device.id}>
                    <h3 className="mb-3 font-semibold">{device.device_name}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {device.fields.map((field) => (
                        <FormGroup key={field.field_id} className="customFormGroup">
                          <FormLabel className="customFormLabel">{field.label}</FormLabel>
                          <TextField
                            type={field.type === 'number' ? 'text' : field.type}
                            className="custumTextField profileInput"
                            name={field.key}
                            placeholder={field.placeholder}
                            value={form[field.key] || ''}
                            inputProps={{
                              inputMode: field.type === 'number' ? 'numeric' : 'text',
                              maxLength: field.maxLength || 5,
                            }}
                            onKeyPress={handleKeyPress}
                            onChange={handleChange}
                          />
                        </FormGroup>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {singleFieldDevices.map((device) => (
                    <div key={device.id}>
                      <h3 className="mb-3 font-semibold">{device.device_name}</h3>
                      {device.fields.map((field) => (
                        <FormGroup key={field.field_id} className="customFormGroup">
                          <FormLabel className="customFormLabel">{field.label}</FormLabel>
                          <TextField
                            type={field.type === 'number' ? 'text' : field.type}
                            className="custumTextField profileInput"
                            name={field.key}
                            placeholder={field.placeholder}
                            value={form[field.key] || ''}
                            inputProps={{
                              inputMode: field.type === 'number' ? 'numeric' : 'text',
                              maxLength: field.maxLength || 5,
                            }}
                            onKeyPress={handleKeyPress}
                            onChange={handleChange}
                          />
                        </FormGroup>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="mt-8 flex md:justify-end">
            <button className="fillButton" type="submit" disabled={btnLoading}>
              {btnLoading && <BtnLoader />} Save Vitals
            </button>
          </div>
        </form>
      </div>
    </CustomContainer>
  );
};

export default AddManualVitals;
